import { ref, onValue, increment, update } from 'firebase/database';
import { db } from '../config/firebase';

interface InteractionDateDetails {
  day: number;
  month: number;
  year: number;
  timestamp: string;
}

export const LikeService = {
  addInteraction: async (type: 'star' | 'like') => {
    try {
      const now = new Date();
      const dateDetails: InteractionDateDetails = {
        day: now.getDate(),
        month: now.getMonth() + 1, // Adding 1 because getMonth() returns 0-11
        year: now.getFullYear(),
        timestamp: now.toISOString(),
      };

      const updates: { [key: string]: unknown } = {
        [`interactions/${type}s/total`]: increment(1),
        'interactions/lastInteraction': dateDetails,
        [`interactions/${type}s/byDate/${dateDetails.year}/${dateDetails.month}/${dateDetails.day}`]:
          increment(1),
      };

      await update(ref(db), updates);
    } catch (error) {
      console.error('Error adding interaction:', error);
      throw error;
    }
  },

  getInteractionCounts: (
    callback: (counts: { stars: number; likes: number }) => void
  ) => {
    const interactionsRef = ref(db, 'interactions');

    return onValue(
      interactionsRef,
      snapshot => {
        const data = snapshot.val() || {
          stars: { total: 0 },
          likes: { total: 0 },
        };
        callback({
          stars: data.stars?.total || 0,
          likes: data.likes?.total || 0,
        });
      },
      error => {
        console.error('Error getting interaction counts:', error);
        throw error;
      }
    );
  },

  getLastInteraction: (
    callback: (details: InteractionDateDetails | null) => void
  ) => {
    const lastInteractionRef = ref(db, 'interactions/lastInteraction');

    return onValue(
      lastInteractionRef,
      snapshot => {
        callback(snapshot.val() || null);
      },
      error => {
        console.error('Error getting last interaction:', error);
        throw error;
      }
    );
  },

  // New method to get interactions by date range
  getInteractionsByDateRange: (
    type: 'star' | 'like',
    startDate: Date,
    endDate: Date,
    callback: (counts: { [date: string]: number }) => void
  ) => {
    const byDateRef = ref(db, `interactions/${type}s/byDate`);

    return onValue(
      byDateRef,
      snapshot => {
        const data = snapshot.val() || {};
        const counts: { [date: string]: number } = {};

        // Iterate through the date range
        const current = new Date(startDate);
        while (current <= endDate) {
          const year = current.getFullYear();
          const month = current.getMonth() + 1;
          const day = current.getDate();

          const count = data[year]?.[month]?.[day] || 0;
          counts[`${year}-${month}-${day}`] = count;

          current.setDate(current.getDate() + 1);
        }

        callback(counts);
      },
      error => {
        console.error('Error getting interactions by date range:', error);
        throw error;
      }
    );
  },
};
